// An admin action: a button that opens the form window (kit/FormWindow, APS-412) with what the
// action needs and staff's reason (required, written to the admin audit log), then confirms.
// The server's answer shows in place; nothing changes until the confirm button.
import { ErrorState as ErrorLine } from '@apsio/ui'
import { type FormEvent, type ReactNode, useState } from 'react'
import { useI18n } from '../i18n'
import { FormWindow, ReasonField } from './kit/FormWindow'
import { looksLikeEmail } from './kit/reason'
import { ErrorDetail, errorMessage } from './states'

export function ActionForm({
  label,
  confirm,
  danger,
  reasonLabel,
  reasonOptional,
  reasonHint,
  noEmails,
  open: controlled,
  onClose,
  children,
  validate,
  onSubmit,
}: {
  /** The button that opens the form. */
  label: string
  /** The confirm button. */
  confirm: string
  danger?: boolean
  /** The reason field's label; defaults to "Reason". */
  reasonLabel?: string
  reasonOptional?: boolean
  /** Under the reason field; defaults to where the reason is kept. */
  reasonHint?: ReactNode
  /** Refuse a reason that looks like it carries an email (erasures: the API refuses it too). */
  noEmails?: boolean
  /**
   * Controlled: the form shows while `open` is true, with no button of its own, so a row menu or
   * a head menu can open it. Cancel and a confirm that succeeds call `onClose`.
   */
  open?: boolean | undefined
  onClose?: (() => void) | undefined
  /** Fields before the reason. */
  children?: ReactNode
  /** What is wrong with the fields before anything is sent (an amount that is not money), if any. */
  validate?: (form: FormData) => string | null
  onSubmit: (reason: string, form: FormData) => Promise<unknown>
}) {
  const { t } = useI18n()
  const [openSelf, setOpenSelf] = useState(false)
  const open = controlled ?? openSelf
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const [refusal, setRefusal] = useState('')
  const [reason, setReason] = useState('')
  const close = () => {
    if (controlled === undefined) setOpenSelf(false)
    setError(undefined)
    setRefusal('')
    onClose?.()
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(undefined)
    setRefusal('')
    if (noEmails && looksLikeEmail(reason)) {
      setRefusal(t('action.reasonNoEmail'))
      return
    }
    const refused = validate?.(new FormData(e.currentTarget))
    if (refused) {
      setRefusal(refused)
      return
    }
    setBusy(true)
    try {
      await onSubmit(reason.trim(), new FormData(e.currentTarget))
      setReason('')
      close()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (!open && controlled !== undefined) return null
  if (!open)
    return (
      <button
        type="button"
        className={danger ? 'btn ghost sm danger' : 'btn sm'}
        onClick={() => setOpenSelf(true)}
      >
        {label}
      </button>
    )
  return (
    <FormWindow
      title={label}
      submitLabel={confirm}
      danger={danger}
      busy={busy}
      onSubmit={submit}
      onCancel={close}
      alert={
        refusal ? (
          <ErrorLine message={refusal} />
        ) : error ? (
          <ErrorLine message={errorMessage(t, error)} detail={<ErrorDetail error={error} />} />
        ) : null
      }
    >
      {children}
      <ReasonField
        value={reason}
        onChange={setReason}
        label={reasonLabel}
        hint={reasonHint}
        optional={reasonOptional}
      />
    </FormWindow>
  )
}
